import { createFileRoute } from '@tanstack/react-router';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { LayoutGrid, TableIcon, ArrowLeft, X } from 'lucide-react';
import { useOperator } from '@/lib/operator-context';
import { SmartInbox } from '@/components/conversations';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useConversationFilters, useConversationData, ViewMode } from './-hooks';
import { ConversationFilters, ConversationTable } from './-components';

function LoadingSpinner() {
  return (
    <Card>
      <CardContent className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </CardContent>
    </Card>
  );
}

function AccessDenied({ role, action }: { role: string; action: string }) {
  const { t } = useTranslation();

  return (
    <Card className="text-center">
      <CardContent className="py-12">
        <div className="text-4xl mb-4">🔒</div>
        <h2 className="text-xl font-semibold mb-2">{t('common.accessDenied')}</h2>
        <p className="text-muted-foreground">
          {t('common.noPermission', { role, action })}
        </p>
      </CardContent>
    </Card>
  );
}

function SelectBusinessPrompt() {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
      <div className="text-4xl mb-4">🏢</div>
      <p>{t('common.selectBusinessFirst')}</p>
    </div>
  );
}

function FlowFilterBadge({
  flowStep,
  onClear,
}: {
  flowStep: string;
  onClear: () => void;
}) {
  return (
    <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-violet-50 dark:bg-violet-950 border border-violet-200 dark:border-violet-800 text-sm text-violet-700 dark:text-violet-300">
      <span>Stage: {flowStep.replace(/-/g, ' ')}</span>
      <button
        onClick={onClear}
        className="hover:bg-violet-100 dark:hover:bg-violet-900 rounded-full p-0.5"
        aria-label="Clear filter"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

function ViewModeToggle({
  viewMode,
  onViewModeChange,
}: {
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
}) {
  const { t } = useTranslation();

  return (
    <Tabs value={viewMode} onValueChange={(v) => onViewModeChange(v as ViewMode)}>
      <TabsList>
        <TabsTrigger value="smart" className="flex items-center gap-2">
          <LayoutGrid className="h-4 w-4" />
          {t('conversations.smartInbox.title')}
        </TabsTrigger>
        <TabsTrigger value="table" className="flex items-center gap-2">
          <TableIcon className="h-4 w-4" />
          {t('conversations.tableView')}
        </TabsTrigger>
      </TabsList>
    </Tabs>
  );
}

function PageHeader({
  canViewFullPII,
  viewMode,
  total,
  selectedBusinessName,
  urlFlowId,
  urlFlowStep,
  onBackToFlowBuilder,
  onClearFlowFilter,
  onViewModeChange,
}: {
  canViewFullPII: boolean;
  viewMode: ViewMode;
  total: number;
  selectedBusinessName: string | null;
  urlFlowId: string | null;
  urlFlowStep: string | null;
  onBackToFlowBuilder: () => void;
  onClearFlowFilter: () => void;
  onViewModeChange: (mode: ViewMode) => void;
}) {
  const { t } = useTranslation();

  const showBackButton = Boolean(urlFlowId);
  const showFlowBadge = Boolean(urlFlowStep);

  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-4">
        {showBackButton && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onBackToFlowBuilder}
            className="flex items-center gap-1"
          >
            <ArrowLeft className="h-4 w-4" />
            {t('flowBuilder.gallery.backToGallery')}
          </Button>
        )}
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t('conversations.title')}</h1>
          <p className="text-muted-foreground">
            {canViewFullPII ? t('common.fullPIIAccess') : t('common.maskedPIIAccess')}
            {viewMode === 'table' && ` | ${t('conversations.conversationCount', { count: total })}`}
          </p>
          <p className="text-muted-foreground mt-1">
            {t('common.viewing')}: <strong>{selectedBusinessName || t('common.allBusinesses')}</strong>
          </p>
        </div>
      </div>

      <div className="flex items-center gap-3">
        {showFlowBadge && (
          <FlowFilterBadge flowStep={urlFlowStep!} onClear={onClearFlowFilter} />
        )}
        <ViewModeToggle viewMode={viewMode} onViewModeChange={onViewModeChange} />
      </div>
    </div>
  );
}

function SmartInboxView({ businessId }: { businessId: string | null }) {
  return (
    <Card className="min-h-[600px]">
      <CardContent className="p-0 h-full">
        {businessId ? (
          <SmartInbox businessId={businessId} />
        ) : (
          <SelectBusinessPrompt />
        )}
      </CardContent>
    </Card>
  );
}

function OperatorConversationsPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { operator, permissions, isLoading: authLoading, selectedBusinessId, selectedBusiness } = useOperator();

  const filters = useConversationFilters();

  const { conversations, loading, error, total, totalPages } = useConversationData({
    operatorExists: Boolean(operator),
    canViewConversations: permissions?.canViewConversations ?? false,
    viewMode: filters.viewMode,
    phoneNumber: filters.phoneNumber,
    state: filters.state,
    dateFrom: filters.dateFrom,
    dateTo: filters.dateTo,
    currentPage: filters.currentPage,
    selectedBusinessId,
  });

  // Early returns for loading and permission states
  if (authLoading) {
    return <LoadingSpinner />;
  }

  if (!operator) {
    return null;
  }

  const canViewConversations = permissions?.canViewConversations ?? false;
  if (!canViewConversations) {
    return <AccessDenied role={operator.role} action={t('conversations.viewConversations')} />;
  }

  const handleBackToFlowBuilder = () => {
    navigate({ to: `/operator/flows?flowId=${filters.urlFlowId}` });
  };

  const isTableView = filters.viewMode === 'table';
  const isSmartView = filters.viewMode === 'smart';

  return (
    <div className="p-6 space-y-6">
      <PageHeader
        canViewFullPII={permissions?.canViewFullPII ?? false}
        viewMode={filters.viewMode}
        total={total}
        selectedBusinessName={selectedBusiness?.name ?? null}
        urlFlowId={filters.urlFlowId}
        urlFlowStep={filters.urlFlowStep}
        onBackToFlowBuilder={handleBackToFlowBuilder}
        onClearFlowFilter={filters.clearFlowFilter}
        onViewModeChange={filters.setViewMode}
      />

      {error && (
        <div className="rounded-md bg-destructive/10 p-4 text-destructive">
          {error}
        </div>
      )}

      {isSmartView && <SmartInboxView businessId={selectedBusinessId} />}

      {isTableView && (
        <>
          <ConversationFilters
            phoneNumber={filters.phoneNumber}
            state={filters.state}
            dateFrom={filters.dateFrom}
            dateTo={filters.dateTo}
            onPhoneNumberChange={filters.setPhoneNumber}
            onStateChange={filters.setState}
            onDateFromChange={filters.setDateFrom}
            onDateToChange={filters.setDateTo}
            onReset={filters.handleReset}
          />

          <ConversationTable
            conversations={conversations}
            loading={loading}
            hasActiveFilters={filters.hasActiveFilters}
            currentPage={filters.currentPage}
            totalPages={totalPages}
            onPageChange={filters.setCurrentPage}
          />
        </>
      )}
    </div>
  );
}

export const Route = createFileRoute('/_auth/operator/conversations/')({
  component: OperatorConversationsPage,
});
